import { forwardRef, PropsWithChildren, ReactNode } from "react";

import classNames from "classnames";

import {
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  makeStyles,
  MenuItem as MuiMenuItem,
  MenuItemProps,
  Theme,
  Tooltip,
  Typography,
  TypographyVariant
} from "@material-ui/core";

import { IoMdHelpCircle } from "react-icons/io";

export interface IDropdownItem {
  key?: string | number;
  label: string | ReactNode;
  icon?: ReactNode;
  endIcon?: ReactNode;
  color?: string;
  withTopBorder?: boolean;
  textVariant?: TypographyVariant;
  disabled?: boolean;
  hidden?: boolean;
  to?: string;
  url?: string;
  onClick?: MenuItemProps["onClick"];
  tooltipText?: string;
  helperText?: string;
}

interface IProps extends IDropdownItem, Omit<MenuItemProps, "onClick" | "key"> {
  className?: string;
  button?: true;
}

const useStyles = makeStyles<
  Theme,
  { withTopBorder?: boolean; color?: string }
>(theme => ({
  item: {
    padding: "3px 20px",
    borderTop: ({ withTopBorder }) =>
      withTopBorder ? `1px solid ${theme.palette.divider}` : undefined,
    color: ({ color }) => color || theme.palette.text.secondary,
    minWidth: 180,
    position: "relative"
  },

  content: {
    display: "flex",
    alignItems: "center"
  },

  icon: {
    minWidth: "unset",
    marginRight: 7,
    color: ({ color }) => color || theme.palette.text.secondary
  },

  endIcon: {
    color: ({ color }) => color || theme.palette.text.secondary
  },

  tooltip: {
    textAlign: "center"
  },

  helperIcon: {
    marginLeft: 5,
    color: theme.palette.grey[500],
    lineHeight: 0
  }
}));

const MenuItem = forwardRef<HTMLLIElement, PropsWithChildren<IProps>>(
  (props, ref) => {
    const {
      label,
      withTopBorder,
      icon,
      endIcon,
      color,
      className,
      textVariant,
      hidden,
      tooltipText,
      helperText,
      ...restProps
    } = props;

    const classes = useStyles({ withTopBorder, color });

    if (hidden) {
      return null;
    }

    function renderItem() {
      return (
        <MuiMenuItem
          ref={ref}
          className={classNames(classes.item, className)}
          {...restProps}
        >
          {icon && <ListItemIcon className={classes.icon}>{icon}</ListItemIcon>}

          <ListItemText>
            <div className={classes.content}>
              <Typography
                color={color ? "inherit" : "textSecondary"}
                variant={textVariant || "caption"}
              >
                {label}
              </Typography>

              {helperText ? (
                <Tooltip title={helperText} placement="right" arrow>
                  <span className={classes.helperIcon}>
                    <IoMdHelpCircle size={16} />
                  </span>
                </Tooltip>
              ) : null}
            </div>
          </ListItemText>

          {endIcon && (
            <ListItemSecondaryAction>
              <div className={classes.endIcon}>{endIcon}</div>
            </ListItemSecondaryAction>
          )}
        </MuiMenuItem>
      );
    }

    return tooltipText ? (
      <Tooltip
        title={tooltipText}
        placement="left"
        classes={{ tooltip: classes.tooltip }}
        arrow
      >
        <span>{renderItem()}</span>
      </Tooltip>
    ) : (
      renderItem()
    );
  }
);

MenuItem.displayName = "MenuItem";

export default MenuItem;
